import { ApiResponse, User, Channel, Theme, Tag } from '../types';
import {
    ApiError,
    DuplicateEmailError,
    AuthenticationError,
    NetworkError,
} from '../types/errors';
import { getApiBaseUrl } from '../utils/apiConfig';
import getConfig from 'next/config';
import { tokenRefreshService } from './tokenRefresh';
import { ARTCAST_CONFIG } from '../constants/artcastConfig';
import logger from '../utils/logger';

const { publicRuntimeConfig } = getConfig() || {};

// Get API URL dynamically to ensure proper client/server values
const getApiUrl = () => getApiBaseUrl();

// Generate public token using the same algorithm as the web app
function generatePublicToken(): string {
    const ENV = process.env.NODE_ENV || 'development';

    // Use browser values if available, fallback to static values for SSR
    const appCodeName =
        (typeof navigator !== 'undefined' && navigator.appCodeName) ||
        'Mozilla';
    const appName =
        (typeof navigator !== 'undefined' && navigator.appName) || 'Netscape';
    const platform =
        (typeof navigator !== 'undefined' && navigator.platform) ||
        'Linux x86_64';

    let publicToken = ENV + appCodeName + appName + platform;

    // Replace specific characters with random numbers as in the web app
    publicToken = publicToken.replace(/M/g, () => {
        return Math.random().toString().substring(2, 6);
    });
    publicToken = publicToken.replace(/N/g, () => {
        return Math.random().toString().substring(2, 6);
    });
    publicToken = publicToken.replace(/d/g, () => {
        return Math.random().toString().substring(2, 6);
    });

    return publicToken;
}

// Get or generate public token
function getPublicToken(): string {
    return (
        publicRuntimeConfig?.niioConfig?.app?.apiTokenPublic ||
        process.env.NEXT_PUBLIC_API_TOKEN_PUBLIC ||
        generatePublicToken()
    );
}

// Get JWT token
function getJwtToken(): string {
    return (
        publicRuntimeConfig?.niioConfig?.app?.apiToken ||
        process.env.NEXT_PUBLIC_API_TOKEN ||
        ''
    );
}

class PairAppServerApi {
    private jwtToken: string;

    constructor() {
        // Try to load JWT token from localStorage on initialization
        this.jwtToken = '';
        if (typeof window !== 'undefined') {
            const storedToken = localStorage.getItem('jwtToken');
            if (storedToken) {
                this.jwtToken = storedToken;
                logger.debug(
                    '[PAIR-APP] API initialized with stored JWT token:',
                    {
                        tokenLength: storedToken.length,
                        tokenPrefix: storedToken.substring(0, 20) + '...',
                    }
                );
            } else {
                logger.debug('[PAIR-APP] API initialized without JWT token');
            }
        }
    }

    setJwtToken(token: string) {
        logger.debug('[PAIR-APP] setJwtToken called with token:', {
            tokenProvided: !!token,
            tokenLength: token ? token.length : 0,
            tokenPrefix: token ? token.substring(0, 20) + '...' : 'none',
        });

        this.jwtToken = token;
        // Also store in localStorage for persistence
        if (typeof window !== 'undefined') {
            localStorage.setItem('jwtToken', token);
            logger.debug('[PAIR-APP] JWT token stored in localStorage');

            // Verify it was stored correctly
            const storedToken = localStorage.getItem('jwtToken');
            logger.debug('[PAIR-APP] Verification - token in localStorage:', {
                stored: !!storedToken,
                matches: storedToken === token,
                length: storedToken ? storedToken.length : 0,
            });
        }
    }

    private getStoredJwtToken(): string {
        // First try instance variable
        if (this.jwtToken) {
            return this.jwtToken;
        }

        // Then try localStorage
        if (typeof window !== 'undefined') {
            const storedToken = localStorage.getItem('jwtToken');
            if (storedToken) {
                // Update instance variable for future use
                this.jwtToken = storedToken;
                return storedToken;
            }
        }

        // Finally fallback to env variable (shouldn't normally be used)
        return getJwtToken();
    }

    private async makeRequest<Res = any>(
        method: string,
        url: string,
        data?: any,
        options?: { skipAuthErrorHandling?: boolean }
    ): Promise<Res | ApiResponse<any>> {
        const fullUrl = getApiUrl() + url;
        const jwtToken = this.getStoredJwtToken();

        logger.debug('[PAIR-APP] API Request:', {
            method: method,
            url: fullUrl,
            hasJWT: !!jwtToken,
            jwtLength: jwtToken ? jwtToken.length : 0,
            jwtPrefix:
                jwtToken && jwtToken.length > 20
                    ? jwtToken.substring(0, 20) + '...'
                    : jwtToken,
        });

        try {
            const requestConfig: RequestInit = {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'X-MyApiTokenHeaderPublic': getPublicToken(),
                    'X-MyApiTokenHeader': jwtToken,
                },
                credentials: 'include',
            };

            if (data) {
                requestConfig.body = JSON.stringify(data);
            }

            const response = await fetch(fullUrl, requestConfig);
            logger.debug(
                '[PAIR-APP] API Response: ' +
                    response.status +
                    ' ' +
                    response.statusText
            );

            if (response.status !== 200) {
                // First try to get the response as text to check for JWT errors
                const responseText = await response.text();

                // Check for JWT validation error
                if (
                    response.status === 400 &&
                    responseText.includes('JWT Not validated')
                ) {
                    logger.debug('[PAIR-APP] JWT token invalid or expired');

                    // Clear the stored JWT token
                    this.jwtToken = '';
                    if (typeof window !== 'undefined') {
                        localStorage.removeItem('jwtToken');
                        localStorage.removeItem('pairUser');
                    }

                    return {
                        __isApiError: true,
                        success: false,
                        error: 'Session expired. Please sign in again.',
                        errorType: 'auth_expired',
                        status: 400,
                        data: undefined,
                    };
                }

                // Try to parse as JSON for other errors
                let errorData;
                try {
                    errorData = JSON.parse(responseText);
                } catch {
                    errorData = { message: responseText || 'Unknown error' };
                }

                // Extract error message from various API response formats
                let errorMessage = 'API Error';
                if (errorData.message) {
                    if (typeof errorData.message === 'string') {
                        errorMessage = errorData.message;
                    } else if (
                        errorData.message.text &&
                        typeof errorData.message.text === 'string'
                    ) {
                        errorMessage = errorData.message.text;
                    }
                } else if (
                    errorData.text &&
                    typeof errorData.text === 'string'
                ) {
                    errorMessage = errorData.text;
                } else if (errorData.errors) {
                    // Handle errors array or object
                    if (typeof errorData.errors === 'string') {
                        errorMessage = errorData.errors;
                    } else if (Array.isArray(errorData.errors)) {
                        errorMessage = errorData.errors.join(', ');
                    } else if (typeof errorData.errors === 'object') {
                        errorMessage = JSON.stringify(errorData.errors);
                    }
                }

                // Check if this is an expected error (like duplicate email)
                const isDuplicateEmail =
                    typeof errorMessage === 'string' &&
                    (errorMessage.includes('already exists') ||
                        errorMessage.includes('email already exists'));

                const isAuthError = response.status === 401;
                const isDeviceAlreadyPaired = response.status === 302;
                const isDeviceAlreadyAssigned =
                    response.status === 500 &&
                    typeof errorMessage === 'string' &&
                    errorMessage.includes(
                        'Device is already assigned to a user'
                    );

                // Enhanced logging for auth-related errors
                if (isAuthError || response.status === 302) {
                    logger.debug('[PAIR-APP] Auth-related error:', {
                        status: response.status,
                        isAuthError: isAuthError,
                        isDeviceAlreadyPaired: isDeviceAlreadyPaired,
                        currentJWT: !!this.jwtToken,
                        jwtLength: this.jwtToken ? this.jwtToken.length : 0,
                        url: fullUrl,
                        errorMessage: errorMessage,
                    });
                }

                // Log appropriately
                if (
                    isDuplicateEmail ||
                    isAuthError ||
                    isDeviceAlreadyPaired ||
                    isDeviceAlreadyAssigned
                ) {
                    logger.debug(
                        '[PAIR-APP] API Expected Error:',
                        errorMessage
                    );
                } else {
                    console.error('[PAIR-APP] API Error:', errorMessage);
                }

                // For auth errors (401), check if user just signed in
                if (isAuthError && !options?.skipAuthErrorHandling) {
                    logger.debug('[PAIR-APP] 401 error detected');

                    // Check if user just signed in (within last 30 seconds)
                    const authTimestamp = localStorage.getItem('authTimestamp');
                    if (authTimestamp) {
                        const timeSinceAuth =
                            Date.now() - parseInt(authTimestamp);
                        if (timeSinceAuth < 30000) {
                            logger.debug(
                                '[PAIR-APP] User just signed in ' +
                                    Math.round(timeSinceAuth / 1000) +
                                    ' seconds ago, ignoring 401 error'
                            );
                            // Return the error but don't throw or clear tokens
                            return {
                                __isApiError: true,
                                success: false,
                                error: errorMessage,
                                errorType: 'temporary_auth_error',
                                status: 401,
                            } as any;
                        }
                    }

                    // If it's been more than 30 seconds, this is a real auth error
                    logger.debug(
                        '[PAIR-APP] Authentication expired or invalid'
                    );
                    const authError = new AuthenticationError(errorMessage);
                    (authError as any).status = 401;
                    throw authError;
                }

                // If auth error handling is skipped, return error result instead of throwing
                if (isAuthError && options?.skipAuthErrorHandling) {
                    return {
                        __isApiError: true,
                        success: false,
                        error: errorMessage,
                        errorType: 'authentication_error',
                        status: 401,
                    } as any;
                }

                // Return error result instead of throwing for other expected errors
                return {
                    __isApiError: true,
                    success: false,
                    error: errorMessage,
                    errorType: isDuplicateEmail
                        ? 'duplicate_email'
                        : isDeviceAlreadyPaired || isDeviceAlreadyAssigned
                        ? 'device_already_paired'
                        : 'api_error',
                    status: response.status,
                    data: undefined,
                };
            }

            const result = await response.json();
            logger.debug('[PAIR-APP] API Success:', result);
            return result;
        } catch (error) {
            // Use console.log for expected errors to avoid Next.js error overlay
            if (
                error instanceof DuplicateEmailError ||
                error instanceof AuthenticationError ||
                (error instanceof ApiError &&
                    error.errorCode === 'DUPLICATE_EMAIL')
            ) {
                logger.debug('[PAIR-APP] API Expected Error (caught):', error);
            } else {
                console.error('[PAIR-APP] API Fetch Error:', error);
            }
            logger.debug(
                '[PAIR-APP] Backend not available, falling back to demo mode'
            );
            throw error;
        }
    }

    // Mock data for demo mode
    private getMockChannels(): Channel[] {
        return [
            {
                id: '1',
                name: 'Contemporary Masters',
                description:
                    'Featuring works from leading contemporary digital artists',
                thumbnail: '/api/placeholder/200/150',
                artworks: [
                    {
                        id: '1',
                        title: 'Digital Dreams',
                        artist: 'Alex Chen',
                        thumbnail: '/api/placeholder/150/150',
                    },
                    {
                        id: '2',
                        title: 'Neon Landscapes',
                        artist: 'Sarah Kim',
                        thumbnail: '/api/placeholder/150/150',
                    },
                ],
            },
            {
                id: '2',
                name: 'Abstract Visions',
                description:
                    'Bold abstract compositions that challenge perception',
                thumbnail: '/api/placeholder/200/150',
                artworks: [
                    {
                        id: '3',
                        title: 'Color Storm',
                        artist: 'Miguel Rodriguez',
                        thumbnail: '/api/placeholder/150/150',
                    },
                    {
                        id: '4',
                        title: 'Geometric Flow',
                        artist: 'Emma Wilson',
                        thumbnail: '/api/placeholder/150/150',
                    },
                ],
            },
            {
                id: '3',
                name: 'Nature Reimagined',
                description: 'Digital interpretations of natural beauty',
                thumbnail: '/api/placeholder/200/150',
                artworks: [
                    {
                        id: '5',
                        title: 'Forest Dreams',
                        artist: 'David Park',
                        thumbnail: '/api/placeholder/150/150',
                    },
                    {
                        id: '6',
                        title: 'Ocean Waves',
                        artist: 'Lisa Chang',
                        thumbnail: '/api/placeholder/150/150',
                    },
                ],
            },
            {
                id: '4',
                name: 'Urban Rhythms',
                description: 'Capturing the pulse of modern city life',
                thumbnail: '/api/placeholder/200/150',
                artworks: [
                    {
                        id: '7',
                        title: 'City Lights',
                        artist: 'James Torres',
                        thumbnail: '/api/placeholder/150/150',
                    },
                    {
                        id: '8',
                        title: 'Street Art',
                        artist: 'Nina Patel',
                        thumbnail: '/api/placeholder/150/150',
                    },
                ],
            },
        ];
    }

    // User creation - simplified for device pairing flow
    async createUser(data: {
        email: string;
        password?: string;
        deviceId: string;
        deviceCode: string;
        socialProvider?: string;
        socialCode?: string;
        socialTicket?: string;
        socialOid?: string;
        firstName?: string;
        lastName?: string;
    }): Promise<ApiResponse<User>> {
        try {
            logger.debug('👤 Creating user for device pairing:', data);

            // Use provided password or generate a random one
            const password =
                data.password || Math.random().toString(36).slice(-12);

            // Step 1: Create user account
            logger.debug('📝 Step 1: Creating user account...');
            const signupData: any = {
                User: {
                    email: data.email,
                    password: password,
                    default_user: true, // Mark as default user for pair app
                },
            };

            // Include names from social profile if available
            if (data.firstName) {
                signupData.User.first_name = data.firstName;
                logger.debug('📱 Setting first_name:', data.firstName);
            }
            if (data.lastName) {
                signupData.User.last_name = data.lastName;
                logger.debug('📱 Setting last_name:', data.lastName);
            }
            // If no names provided, backend will generate them from email

            // Add social provider data if available
            if (data.socialProvider) {
                signupData.User.social_provider = data.socialProvider;

                // Handle social authentication fields based on provider and ticket availability
                if (data.socialTicket) {
                    // If we have a ticket, it means SocialProfile already exists
                    // Just send the ticket to link the user to the existing SocialProfile
                    signupData.User.social_code = data.socialTicket;
                    logger.debug(
                        '📱 Including social code for linking existing SocialProfile:',
                        data.socialProvider
                    );
                } else if (data.socialProvider === 'apple' && data.socialCode) {
                    // Legacy Apple flow - use social_code with code
                    signupData.User.social_code = data.socialCode;
                    logger.debug(
                        '📱 Including Apple auth code as social_code:',
                        data.socialCode
                    );
                } else if (data.socialCode) {
                    // V1 flow with social_code
                    signupData.User.social_code = data.socialCode;
                    logger.debug(
                        '📱 Including social provider V1 with code:',
                        data.socialProvider
                    );
                }

                // Only add OID if we don't have a ticket (ticket means SocialProfile already exists)
                if (data.socialOid && !data.socialTicket) {
                    signupData.User.social_oid = data.socialOid;
                    logger.debug(
                        '📱 Including social OID for new SocialProfile creation:',
                        data.socialOid
                    );
                }
            }

            logger.debug(
                '📱 Complete signup payload:',
                JSON.stringify(signupData, null, 2)
            );

            const signupResult = await this.makeRequest(
                'POST',
                '/users/signUp',
                signupData
            );

            // Check if this is an error response (don't throw!)
            if (signupResult.__isApiError) {
                logger.debug(
                    '📛 API returned error in createUser:',
                    signupResult
                );
                // Return the error response, don't throw
                return signupResult;
            }

            logger.debug('✅ User created successfully:', signupResult);

            // Step 2: Sign in to get JWT token
            logger.debug('🔐 Step 2: Signing in to get JWT token...');
            const signinResult = await this.makeRequest(
                'POST',
                '/users/signIn',
                {
                    User: {
                        email: data.email,
                        password: password,
                    },
                }
            );

            // Check if sign-in also returned an error
            if (signinResult.__isApiError) {
                logger.debug('📛 API returned error in signin:', signinResult);
                return signinResult;
            }

            logger.debug('✅ Sign in successful, JWT token obtained');
            logger.debug('📦 SignIn Result:', signinResult);
            logger.debug(
                '📦 Preview Pass Days from signin:',
                signinResult.data?.preview_pass_x_days ||
                    signinResult.preview_pass_x_days
            );

            // Extract JWT token from response and store it
            const jwtToken =
                signinResult.token ||
                (signinResult.data && signinResult.data.token);
            if (jwtToken) {
                logger.debug(
                    '🎫 JWT Token received, storing for subsequent requests...'
                );
                this.setJwtToken(jwtToken);

                // Store temporary auth data for refresh (email auth only)
                // This is a temporary solution until proper refresh tokens are implemented
                // Only store if we have both email and password (not for social auth)
                if (data.email && data.password) {
                    tokenRefreshService.storeTemporaryAuthData(
                        data.email,
                        data.password
                    );
                }
            }

            return {
                success: true,
                data: {
                    id:
                        (signupResult.data && signupResult.data.id) ||
                        signupResult.id,
                    email: data.email,
                    name:
                        (signupResult.data && signupResult.data.display_name) ||
                        data.email.split('@')[0],
                    first_name:
                        signinResult.data?.first_name ||
                        signinResult.first_name ||
                        data.firstName ||
                        '',
                    last_name:
                        signinResult.data?.last_name ||
                        signinResult.last_name ||
                        data.lastName ||
                        '',
                    token: jwtToken, // Include token in response
                    preview_pass_x_days:
                        signinResult.data?.preview_pass_x_days ||
                        signinResult.preview_pass_x_days,
                    account_type:
                        signinResult.data?.account_type ||
                        signinResult.account_type,
                },
            };
        } catch (error) {
            console.error(
                '❌ User creation/signin failed with exception:',
                error
            );

            // Return error response for network/connection issues
            return {
                __isApiError: true,
                success: false,
                error: 'Unable to connect to the server. Please check your internet connection and try again.',
                errorType: 'network_error',
                status: 0,
                data: undefined,
            };
        }
    }

    // User sign in - to get JWT token for existing users
    async signInUser(data: {
        email: string;
        password: string;
    }): Promise<ApiResponse<User>> {
        try {
            logger.debug('🔐 Signing in user:', data.email);

            // Use form data format for signin as it seems to work better
            const formData = new URLSearchParams();
            formData.append('User[email]', data.email);
            formData.append('User[password]', data.password);

            const fullUrl = getApiUrl() + '/users/signIn';
            logger.debug('[PAIR-APP] API Request: POST ' + fullUrl);

            const response = await fetch(fullUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-MyApiTokenHeaderPublic': getPublicToken(),
                    'X-MyApiTokenHeader': this.getStoredJwtToken(),
                },
                body: formData,
                credentials: 'include',
            });

            logger.debug(
                '[PAIR-APP] API Response: ' +
                    response.status +
                    ' ' +
                    response.statusText
            );

            if (response.status !== 200) {
                const errorData = await response.json().catch(() => {
                    return { message: 'Unknown error' };
                });

                // Extract error message from various API response formats
                let errorMessage = 'API Error';
                if (errorData.message) {
                    if (typeof errorData.message === 'string') {
                        errorMessage = errorData.message;
                    } else if (
                        errorData.message.text &&
                        typeof errorData.message.text === 'string'
                    ) {
                        errorMessage = errorData.message.text;
                    }
                } else if (
                    errorData.text &&
                    typeof errorData.text === 'string'
                ) {
                    errorMessage = errorData.text;
                } else if (errorData.errors) {
                    // Handle errors array or object
                    if (typeof errorData.errors === 'string') {
                        errorMessage = errorData.errors;
                    } else if (Array.isArray(errorData.errors)) {
                        errorMessage = errorData.errors.join(', ');
                    } else if (typeof errorData.errors === 'object') {
                        errorMessage = JSON.stringify(errorData.errors);
                    }
                }

                // Check if this is an expected error (like duplicate email)
                const isDuplicateEmail =
                    typeof errorMessage === 'string' &&
                    (errorMessage.includes('already exists') ||
                        errorMessage.includes('email already exists'));

                const isAuthError = response.status === 401;
                const isDeviceAlreadyPaired = response.status === 302;
                const isDeviceAlreadyAssigned =
                    response.status === 500 &&
                    typeof errorMessage === 'string' &&
                    errorMessage.includes(
                        'Device is already assigned to a user'
                    );

                // Enhanced logging for auth-related errors
                if (isAuthError || response.status === 302) {
                    logger.debug('[PAIR-APP] Auth-related error:', {
                        status: response.status,
                        isAuthError: isAuthError,
                        isDeviceAlreadyPaired: isDeviceAlreadyPaired,
                        currentJWT: !!this.jwtToken,
                        jwtLength: this.jwtToken ? this.jwtToken.length : 0,
                        url: fullUrl,
                        errorMessage: errorMessage,
                    });
                }

                // Log appropriately
                if (
                    isDuplicateEmail ||
                    isAuthError ||
                    isDeviceAlreadyPaired ||
                    isDeviceAlreadyAssigned
                ) {
                    logger.debug(
                        '[PAIR-APP] API Expected Error:',
                        errorMessage
                    );
                } else {
                    console.error('[PAIR-APP] API Error:', errorMessage);
                }

                // For auth errors (401), just throw the error
                // No automatic re-authentication attempts
                if (isAuthError) {
                    logger.debug('[PAIR-APP] 401 error in signInUser');
                    const authError = new AuthenticationError(errorMessage);
                    (authError as any).status = 401;
                    throw authError;
                }

                // Return error result instead of throwing for other expected errors
                return {
                    __isApiError: true,
                    success: false,
                    error: errorMessage,
                    errorType: isDuplicateEmail
                        ? 'duplicate_email'
                        : isDeviceAlreadyPaired || isDeviceAlreadyAssigned
                        ? 'device_already_paired'
                        : 'api_error',
                    status: response.status,
                    data: undefined,
                };
            }

            const result = await response.json();
            logger.debug('[PAIR-APP] API Success:', result);

            logger.debug('✅ Sign in successful');
            logger.debug('📦 SignIn Result:', result);
            logger.debug(
                '📦 Preview Pass Days from signin:',
                result.data?.preview_pass_x_days || result.preview_pass_x_days
            );

            // Extract JWT token from response and store it
            const jwtToken = result.token || (result.data && result.data.token);
            if (jwtToken) {
                logger.debug(
                    '🎫 JWT Token received, storing for subsequent requests...'
                );
                this.setJwtToken(jwtToken);

                // Store temporary auth data for refresh (email auth only)
                // This is a temporary solution until proper refresh tokens are implemented
                // Only store if we have both email and password (not for social auth)
                if (data.email && data.password) {
                    tokenRefreshService.storeTemporaryAuthData(
                        data.email,
                        data.password
                    );
                }
            }

            return {
                success: true,
                data: {
                    id: (result.data && result.data.id) || result.id,
                    email: result.data?.email || result.email || data.email,
                    name:
                        (result.data && result.data.display_name) || data.email,
                    first_name:
                        result.data?.first_name || result.first_name || '',
                    last_name: result.data?.last_name || result.last_name || '',
                    token: jwtToken,
                    preview_pass_x_days:
                        result.data?.preview_pass_x_days ||
                        result.preview_pass_x_days,
                    account_type:
                        result.data?.account_type || result.account_type,
                },
            };
        } catch (error) {
            console.error('❌ Sign in failed:', error);

            // Return error response for network/connection issues
            return {
                __isApiError: true,
                success: false,
                error: 'Unable to connect to the server. Please check your internet connection and try again.',
                errorType: 'network_error',
                status: 0,
                data: undefined,
            };
        }
    }

    // Device pairing - matches the PHP attachDevice method exactly
    async pairDevice(data: {
        deviceId: string;
        deviceCode: string;
        userId: string;
    }): Promise<ApiResponse<any>> {
        try {
            logger.debug('🔗 Pairing device:', data);

            const result = await this.makeRequest(
                'POST',
                '/devices/attachDevice',
                {
                    deviceId: data.deviceId,
                    deviceCode: data.deviceCode,
                    userId: data.userId,
                    deviceName: 'NIIO Player', // Default device name
                    deviceLocation: 'Home', // Default location
                }
            );

            // Check if this is an error response from the API
            if (result.__isApiError) {
                logger.debug('📛 Device pairing returned error:', result);
                return result; // Return the error response
            }

            return { success: true, data: result };
        } catch (error) {
            console.error('❌ Device pairing failed:', error);
            logger.debug('🎭 Falling back to demo mode for device pairing');

            // Demo mode fallback
            return {
                success: true,
                data: {
                    message: 'Device paired successfully (demo mode)',
                    deviceId: data.deviceId,
                },
            };
        }
    }

    // Get devices for the current user - used to check if a device is already paired
    async getDevicesByUser(): Promise<ApiResponse<any[]>> {
        try {
            console.log('📱 Fetching user devices');

            const result = await this.makeRequest(
                'POST',
                '/devices/getDevicesByUser',
                {}
            );

            // Check if this is an error response from the API
            if (result.__isApiError) {
                console.log('📛 Get devices returned error:', result);
                return { success: false, data: [], error: result.error };
            }

            // The API returns { devices: [...] }
            const devices = result.devices || result.data?.devices || [];
            console.log('📱 Found', devices.length, 'devices for user');

            return { success: true, data: devices };
        } catch (error) {
            console.error('❌ Get devices failed:', error);
            return { success: false, data: [], error: 'Failed to get devices' };
        }
    }

    // Search channels based on keywords, themes, and tags
    async searchChannels(
        searchKeyword: string,
        searchThemes: string[] = [],
        selectedTags: string[] = [],
        limit: number = ARTCAST_CONFIG.MAX_DISPLAY_COUNT
    ): Promise<ApiResponse<Channel[]>> {
        try {
            // Combine keyword with tags (tags with # prefix)
            let finalKeyword = searchKeyword || '';
            if (selectedTags && selectedTags.length > 0) {
                const tagString = selectedTags
                    .map((tag) => `#${tag}`)
                    .join(' ');
                finalKeyword = finalKeyword
                    ? `${finalKeyword} ${tagString}`
                    : tagString;
            }

            logger.debug('🔍 Searching channels with:', {
                keyword: finalKeyword,
                themes: searchThemes,
                limit,
            });

            // If no search criteria provided, just return empty to trigger featured channels fallback
            if (
                (!finalKeyword || !finalKeyword.trim()) &&
                (!searchThemes || searchThemes.length === 0)
            ) {
                logger.debug(
                    '📋 No search criteria provided, will use featured channels'
                );
                return { success: true, data: [] };
            }

            // Prepare the request data
            const requestData: any = {
                searchFeatured: true,
                type: 'AC', // Add type parameter as requested
            };

            // Add keyword if provided
            if (finalKeyword && finalKeyword.trim()) {
                requestData.searchKeyword = finalKeyword.trim();
            }

            // Add themes if provided
            if (searchThemes && searchThemes.length > 0) {
                requestData.searchThemes = searchThemes;
            }

            // Make the API call
            const fullUrl = `/channels/searchChannels/${limit}?load=1`;
            const result = await this.makeRequest('POST', fullUrl, requestData);

            logger.debug('🔍 Search channels raw result:', result);

            // Check if this is an error response
            if (result.__isApiError) {
                console.error('❌ Search channels failed:', result.error);
                return { success: false, error: result.error };
            }

            // Transform the result to match our Channel interface
            const channels: Channel[] = [];

            // The searchChannels endpoint returns data directly
            let channelsArray: any[] | null = null;

            // Try different response structures based on what the API returns
            if (result && result.data) {
                // The API wraps the response in a data field
                const data = result.data;
                if (Array.isArray(data)) {
                    logger.debug('✅ Found channels array at result.data');
                    channelsArray = data;
                } else if (data.channels && Array.isArray(data.channels)) {
                    logger.debug('✅ Found channels at result.data.channels');
                    channelsArray = data.channels;
                } else if (data.data && Array.isArray(data.data)) {
                    logger.debug('✅ Found channels at result.data.data');
                    channelsArray = data.data;
                }
            } else if (Array.isArray(result)) {
                logger.debug('✅ Found channels as direct array');
                channelsArray = result;
            }

            if (channelsArray) {
                channelsArray.forEach((channel) => {
                    // Aggregate artworks from both direct artworks and collections
                    const aggregatedArtworks =
                        this.aggregateChannelArtworks(channel);

                    // First spread the channel to get all properties, then override with our specific ones
                    const processedChannel = {
                        ...channel, // Include all other fields from the API first
                        id: channel.id,
                        name: channel.name,
                        description: channel.description || 'Curated Artcast',
                        thumbnail: channel.thumbnail,
                        artworks: aggregatedArtworks, // Override with aggregated artworks (must come after spread)
                        creator: channel.creator,
                        uploader: channel.uploader,
                        collections: channel.collections, // Preserve collections for reference
                    };

                    channels.push(processedChannel);
                });
            }

            // If no channels found, return empty array (will trigger fallback to featured)
            if (!channelsArray || channels.length === 0) {
                logger.debug('⚠️ No channels found in search results');
            }

            logger.debug('✅ Search returned', channels.length, 'channels');
            return { success: true, data: channels };
        } catch (error) {
            console.error('❌ Search channels failed:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Search failed',
            };
        }
    }

    // Helper function to aggregate artworks from channel and collections
    private aggregateChannelArtworks(channel: any): any[] {
        const artworks: any[] = [];

        logger.debug(
            `🔍 Aggregating artworks for channel: ${channel.name || channel.id}`
        );

        // Add direct artworks from the channel
        if (channel.artworks && Array.isArray(channel.artworks)) {
            logger.debug(`  📎 Direct artworks: ${channel.artworks.length}`);
            artworks.push(...channel.artworks);
        } else {
            logger.debug(`  📎 No direct artworks found`);
        }

        // Add artworks from collections
        if (channel.collections && Array.isArray(channel.collections)) {
            logger.debug(
                `  📦 Found ${channel.collections.length} collection(s)`
            );
            channel.collections.forEach((collection: any, index: number) => {
                if (collection.artworks && Array.isArray(collection.artworks)) {
                    logger.debug(
                        `    - Collection ${index + 1}: ${
                            collection.artworks.length
                        } artworks`
                    );
                    artworks.push(...collection.artworks);
                } else {
                    logger.debug(`    - Collection ${index + 1}: No artworks`);
                }
            });
        } else {
            logger.debug(`  📦 No collections found`);
        }

        logger.debug(`  ✅ Total aggregated artworks: ${artworks.length}`);

        return artworks;
    }

    // Get featured channels for AI selection
    async getFeaturedChannels(): Promise<ApiResponse<Channel[]>> {
        try {
            logger.debug('📺 Fetching featured channels...');

            // Step 1: Determine environment suffix from config or env
            const env =
                publicRuntimeConfig?.niioConfig?.env ||
                process.env.NEXT_PUBLIC_ENV ||
                process.env.ENV ||
                'development';
            let suffix = '';

            if (env === 'development' || env === 'dev') {
                suffix = '_dev';
            } else if (env === 'pre' || env === 'staging') {
                suffix = '_pre';
            } else if (env === 'prod' || env === 'production') {
                // Production gets no suffix
                suffix = '';
            }
            // Default to no suffix for any other environment

            logger.debug(
                '🌍 Environment:',
                env,
                '→ suffix:',
                suffix || '(none)'
            );

            const jsonUrl = `https://niio-mobile-app.s3.us-east-1.amazonaws.com/atmospheres-by-param${suffix}.json`;
            logger.debug('📥 Fetching atmospheres config from:', jsonUrl);

            // Step 2: Fetch the JSON configuration
            const configResponse = await fetch(jsonUrl);
            if (!configResponse.ok) {
                throw new Error(
                    `Failed to fetch atmospheres config: ${configResponse.status}`
                );
            }

            const config = await configResponse.json();
            logger.debug('📋 Atmospheres config loaded:', config);

            // Step 3: Get artFan channel IDs
            const artFanChannelIds =
                config.autoSubscriptionByRole?.artFan || [];
            logger.debug('🎯 ArtFan channel IDs:', artFanChannelIds);

            if (artFanChannelIds.length === 0) {
                console.warn('⚠️ No artFan channels found in config');
                return { success: true, data: [] };
            }

            // Step 4: Fetch channels using getChannelsByRange
            logger.debug('📡 Fetching channels by range...');

            // Convert to URL-encoded format
            const formData = new URLSearchParams();
            // Send as individual params like channelsRange[]=6941&channelsRange[]=5113
            artFanChannelIds.forEach((id: number) => {
                formData.append('channelsRange[]', id.toString());
            });

            const fullUrl = getApiUrl() + '/channels/getChannelsByRange?load=1';
            const response = await fetch(fullUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-MyApiTokenHeaderPublic': getPublicToken(),
                    'X-MyApiTokenHeader': this.getStoredJwtToken(),
                },
                body: formData.toString(),
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch channels: ${response.status}`);
            }

            const result = await response.json();
            logger.debug('🔍 Raw API result:', result);

            // Transform the result to match our Channel interface
            // Try different response structures as the API format varies between endpoints
            const channels: Channel[] = [];

            // Try multiple possible response structures
            let channelsArray: any[] | null = null;

            // Try 1: result.data.channels (most common structure for getChannelsByRange)
            if (
                result.data &&
                result.data.channels &&
                Array.isArray(result.data.channels)
            ) {
                logger.debug('✅ Found channels at result.data.channels');
                channelsArray = result.data.channels;
            }
            // Try 2: result.data as array (like getMarketChannels returns)
            else if (result.data && Array.isArray(result.data)) {
                logger.debug('✅ Found channels array at result.data');
                channelsArray = result.data;
            }
            // Try 3: result.channels (direct array)
            else if (result.channels && Array.isArray(result.channels)) {
                logger.debug('✅ Found channels at result.channels');
                channelsArray = result.channels;
            }
            // Try 4: result as direct array
            else if (Array.isArray(result)) {
                logger.debug('✅ Found channels as direct array');
                channelsArray = result;
            }
            // Try 5: result.data.data.channels (nested structure - less common)
            else if (
                result.data &&
                result.data.data &&
                result.data.data.channels &&
                Array.isArray(result.data.data.channels)
            ) {
                logger.debug('✅ Found channels at result.data.data.channels');
                channelsArray = result.data.data.channels;
            }

            if (channelsArray) {
                // Process each channel in the array
                channelsArray.forEach((channel) => {
                    // Aggregate artworks from both direct artworks and collections
                    const aggregatedArtworks =
                        this.aggregateChannelArtworks(channel);

                    logger.debug(`📺 Channel ${channel.id}:`, {
                        id: channel.id,
                        name: channel.name,
                        directArtworks: channel.artworks?.length || 0,
                        collections: channel.collections?.length || 0,
                        totalArtworks: aggregatedArtworks.length,
                    });

                    const processedChannel = {
                        ...channel, // Include all other fields from the API first
                        artworks: aggregatedArtworks, // Override with aggregated artworks (must come after spread)
                    };

                    logger.debug(`📦 Processed channel:`, {
                        name: processedChannel.name,
                        originalArtworksCount: channel.artworks?.length || 0,
                        aggregatedArtworksCount:
                            processedChannel.artworks.length,
                        finalArtworks: processedChannel.artworks,
                    });

                    channels.push(processedChannel);
                });
            } else {
                console.warn(
                    '❌ Could not find channels array in API response'
                );
                console.warn('📋 Response structure:', {
                    isArray: Array.isArray(result),
                    hasData: !!result.data,
                    dataIsArray: Array.isArray(result.data),
                    keys: Object.keys(result || {}),
                    dataKeys: result.data ? Object.keys(result.data) : [],
                });
            }

            logger.debug(
                '✅ Featured channels fetched:',
                channels.length,
                'channels'
            );

            return { success: true, data: channels };
        } catch (error) {
            console.error('❌ Failed to load channels:', error);
            logger.debug('🎭 Falling back to demo mode for channels');

            // Demo mode fallback
            return { success: true, data: this.getMockChannels() };
        }
    }

    // Fetch specific channel(s) by ID for gift artcasts
    async fetchGiftChannel(
        channelIds: string | string[]
    ): Promise<ApiResponse<Channel[]>> {
        try {
            // Ensure channelIds is an array
            const ids = Array.isArray(channelIds) ? channelIds : [channelIds];

            logger.debug('🎁 Fetching gift channel(s):', ids);

            // Convert to URL-encoded format
            const formData = new URLSearchParams();
            ids.forEach((id) => {
                formData.append('channelsRange[]', id.toString());
            });
            // Skip content validation for gift channels
            formData.append('skipContentValidation', 'true');

            const fullUrl = getApiUrl() + '/channels/getChannelsByRange?load=1';
            const response = await fetch(fullUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-MyApiTokenHeaderPublic': getPublicToken(),
                    'X-MyApiTokenHeader': this.getStoredJwtToken(),
                },
                body: formData.toString(),
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error(
                    `Failed to fetch gift channel: ${response.status}`
                );
            }

            const result = await response.json();
            logger.debug('🎁 Gift channel API response:', result);

            // Parse the response to get channels
            let channelsArray: any[] = [];

            if (
                result.data &&
                result.data.channels &&
                Array.isArray(result.data.channels)
            ) {
                channelsArray = result.data.channels;
            } else if (result.data && Array.isArray(result.data)) {
                channelsArray = result.data;
            } else if (result.channels && Array.isArray(result.channels)) {
                channelsArray = result.channels;
            } else if (Array.isArray(result)) {
                channelsArray = result;
            }

            // Process channels and aggregate artworks
            const channels: Channel[] = [];

            if (channelsArray && channelsArray.length > 0) {
                channelsArray.forEach((channel) => {
                    const aggregatedArtworks =
                        this.aggregateChannelArtworks(channel);

                    const processedChannel = {
                        ...channel,
                        id: channel.id,
                        name: channel.name,
                        description: channel.description || 'Gift Artcast',
                        thumbnail: channel.thumbnail,
                        artworks: aggregatedArtworks,
                        creator: channel.creator,
                        uploader: channel.uploader,
                        collections: channel.collections,
                        isGift: true, // Mark as gift channel
                    };

                    channels.push(processedChannel);
                });
            }

            logger.debug('🎁 Processed gift channels:', channels);
            return { success: true, data: channels };
        } catch (error) {
            console.error('❌ Failed to fetch gift channel:', error);
            return {
                success: false,
                error:
                    error instanceof Error
                        ? error.message
                        : 'Failed to fetch gift channel',
            };
        }
    }

    // Subscribe to channels
    async subscribeToChannels(channelIds: string[]): Promise<ApiResponse<any>> {
        try {
            logger.debug('📺 Subscribing to channels:', channelIds);

            const result = await this.makeRequest(
                'POST',
                '/channelSubscriptions/subscribeToChannel',
                {
                    onBoardingChannelsIds: channelIds,
                }
            );

            logger.debug('✅ Successfully subscribed to channels');
            return { success: true, data: result };
        } catch (error) {
            console.error('❌ Failed to subscribe to channels:', error);
            return {
                success: false,
                error:
                    error instanceof Error
                        ? error.message
                        : 'Failed to subscribe to channels',
            };
        }
    }

    // Add artwork to favorites/wishlist
    async toggleWishlist(artworkId: string): Promise<ApiResponse<any>> {
        try {
            logger.debug('❤️ Adding artwork to wishlist:', artworkId);

            const result = await this.makeRequest(
                'POST',
                '/artworks/toggleWishlist',
                {
                    artworkId: artworkId,
                    forceAdd: true, // Ensure it's always added, not toggled
                }
            );

            logger.debug('✅ Successfully added artwork to wishlist');
            return { success: true, data: result };
        } catch (error) {
            console.error('❌ Failed to add artwork to wishlist:', error);
            return {
                success: false,
                error:
                    error instanceof Error
                        ? error.message
                        : 'Failed to add to wishlist',
            };
        }
    }

    // Subscribe to a single channel (convenience method)
    async subscribeToChannel(
        userId: string,
        channelId: string
    ): Promise<ApiResponse<any>> {
        return this.subscribeToChannels([channelId]);
    }

    // Add artwork to favorites (convenience method)
    async addToFavorites(
        userId: string,
        artworkId: string
    ): Promise<ApiResponse<any>> {
        return this.toggleWishlist(artworkId);
    }

    // Sign out user (end server session)
    async signOut(): Promise<ApiResponse<any>> {
        try {
            logger.debug('🚪 Signing out user from server...');

            // Call the server signOut endpoint
            const result = await this.makeRequest(
                'POST',
                '/users/signOut',
                {},
                {
                    skipAuthErrorHandling: true, // Don't try to refresh token when signing out
                }
            );

            // Clear JWT token immediately after successful signout
            this.jwtToken = '';

            logger.debug('✅ Successfully signed out from server');
            return { success: true, data: result };
        } catch (error) {
            console.error('❌ Failed to sign out from server:', error);
            // Even if server signout fails, we should clear local state
            this.jwtToken = '';
            return {
                success: false,
                error:
                    error instanceof Error
                        ? error.message
                        : 'Failed to sign out from server',
            };
        }
    }

    // Associate coupon with user account
    async associateCouponForUser(
        provider: 'apple' | 'charge_bee',
        couponCode: string
    ): Promise<ApiResponse<any>> {
        try {
            logger.debug('🎟️ Associating coupon with user:', {
                provider,
                couponCode,
            });

            const result = await this.makeRequest(
                'POST',
                `/coupons/associateCouponForUser/${provider}/${encodeURIComponent(
                    couponCode
                )}`,
                {}
            );

            logger.debug('✅ Successfully associated coupon with user');
            return { success: true, data: result };
        } catch (error) {
            console.error('❌ Failed to associate coupon:', error);
            return {
                success: false,
                error:
                    error instanceof Error
                        ? error.message
                        : 'Failed to associate coupon',
            };
        }
    }

    // Set device queue using the new PHP method
    async setDeviceQueue(data: {
        deviceId: string;
        channelId: string;
        artworks?: any[];
    }): Promise<ApiResponse<any>> {
        try {
            logger.debug('🎶 Setting device queue:', data);

            // Create artworks array with correct structure
            const artworksForQueue = (data.artworks || []).map((artwork) => ({
                artworkID: artwork.id,
                originalChannelID: data.channelId,
                downloaded: false,
            }));

            // Correct queue structure: array format without "sections" wrapper
            const queueData = [
                {
                    section: 'channel',
                    channelID: data.channelId,
                    artworks: artworksForQueue,
                },
            ];

            logger.debug('📝 Queue data structure:', queueData);

            const result = await this.makeRequest(
                'POST',
                '/devices/setQueuePlaylist',
                {
                    deviceId: data.deviceId,
                    queuePlaylist: queueData,
                }
            );

            logger.debug('✅ Device queue set:', result);

            return { success: true, data: result };
        } catch (error) {
            console.error('❌ Failed to set queue:', error);
            logger.debug('🎭 Falling back to demo mode for queue setting');

            // Demo mode fallback
            return {
                success: true,
                data: {
                    message: 'Queue set successfully (demo mode)',
                    channelId: data.channelId,
                },
            };
        }
    }

    // Start Preview Pass
    async startPreviewPass(): Promise<ApiResponse<{ end_date: string }>> {
        try {
            logger.debug('🎁 Activating preview pass...');
            const result = await this.makeRequest(
                'POST',
                '/users/startPreviewPass'
            );

            // Check if this is an error response (e.g., already activated)
            if (result.__isApiError) {
                logger.debug(
                    '📛 Preview pass activation returned error:',
                    result
                );
                // If it's a 500 error, it likely means already activated
                if (result.status === 500) {
                    return {
                        success: false,
                        error: 'Preview pass already activated',
                        data: undefined,
                    };
                }
                return result;
            }

            return {
                success: true,
                data: result,
            };
        } catch (error) {
            console.error('❌ Failed to activate preview pass:', error);
            return {
                success: false,
                error: 'Failed to activate preview pass',
            };
        }
    }

    // Get Featured Categories
    async getFeaturedCategories(): Promise<ApiResponse<any[]>> {
        try {
            logger.debug('[PAIR-APP] Fetching featured categories...');
            const result = await this.makeRequest(
                'POST',
                '/categories/getAll',
                { featured: true }
            );

            // Check if this is an error response
            if (result.__isApiError) {
                logger.debug(
                    '[PAIR-APP] Featured categories fetch error:',
                    result.error
                );
                return result;
            }

            logger.debug(
                '[PAIR-APP] Featured categories fetched successfully!',
                result
            );
            return {
                success: true,
                data: result.data || [],
            };
        } catch (error) {
            console.error(
                '[PAIR-APP] Failed to fetch featured categories:',
                error
            );
            return {
                success: false,
                error:
                    error instanceof Error
                        ? error.message
                        : 'Failed to fetch featured categories',
            };
        }
    }

    // Get All Genres
    async getAllGenres(): Promise<ApiResponse<any[]>> {
        try {
            logger.debug('[PAIR-APP] Fetching all genres...');
            const result = await this.makeRequest(
                'POST',
                '/genres/getAll',
                {},
                {
                    skipAuthErrorHandling: true,
                }
            );

            // Check if this is an error response
            if (result.__isApiError) {
                logger.debug('[PAIR-APP] Genres fetch error:', result.error);
                return result;
            }

            logger.debug('[PAIR-APP] Genres fetched successfully!', result);
            return {
                success: true,
                data: result.data || [],
            };
        } catch (error) {
            console.error('[PAIR-APP] Failed to fetch genres:', error);

            // Re-throw authentication errors so they can be handled properly
            if (error instanceof AuthenticationError) {
                throw error;
            }

            return {
                success: false,
                error:
                    error instanceof Error
                        ? error.message
                        : 'Failed to fetch genres',
            };
        }
    }

    // Get All Themes
    async getThemes(): Promise<ApiResponse<Theme[]>> {
        try {
            logger.debug('[PAIR-APP] Fetching all themes...');
            const result = await this.makeRequest(
                'GET',
                '/themes/getAll',
                undefined,
                {
                    skipAuthErrorHandling: true,
                }
            );

            logger.debug('[PAIR-APP] Themes fetched successfully!', result);
            return {
                success: true,
                data: result.data || [],
            };
        } catch (error) {
            console.error('[PAIR-APP] Failed to fetch themes:', error);

            // Re-throw authentication errors so they can be handled properly
            if (error instanceof AuthenticationError) {
                throw error;
            }

            return {
                success: false,
                error:
                    error instanceof Error
                        ? error.message
                        : 'Failed to fetch themes',
            };
        }
    }

    // Get Top Tags
    async getTopTags(count: number = 20): Promise<ApiResponse<Tag[]>> {
        try {
            logger.debug(`[PAIR-APP] Fetching top ${count} tags...`);
            const result = await this.makeRequest(
                'POST',
                `/tags/getTopTags/${count}`,
                {
                    fetchChannels: true,
                    featured: true,
                },
                {
                    skipAuthErrorHandling: true,
                }
            );

            logger.debug('[PAIR-APP] Tags fetched successfully!', result);
            return {
                success: true,
                data: result.tags || [],
            };
        } catch (error) {
            console.error('[PAIR-APP] Failed to fetch tags:', error);

            // Re-throw authentication errors so they can be handled properly
            if (error instanceof AuthenticationError) {
                throw error;
            }

            return {
                success: false,
                error:
                    error instanceof Error
                        ? error.message
                        : 'Failed to fetch tags',
            };
        }
    }

    // V2 Social login with proper Google OAuth validation
    async socialSignInV2(
        provider: 'google' | 'apple',
        code: string,
        userData?: {
            email?: string;
            firstName?: string;
            lastName?: string;
            oid?: string;
        }
    ): Promise<ApiResponse<User>> {
        try {
            logger.debug(`🔐 Attempting social sign in V2 with ${provider}...`);

            const body: any = {
                provider: provider,
                code: code,
                action: 'login',
                view: 'web',
            };

            if (userData?.oid) {
                body.oid = userData.oid;
            }

            const formData = new URLSearchParams();
            Object.keys(body).forEach((key) => {
                if (body[key] !== undefined && body[key] !== null) {
                    formData.append(key, body[key]);
                }
            });

            const fullUrl = getApiUrl() + `/users/externalSignInV2`;
            const publicToken = getPublicToken();
            logger.debug(
                '[PAIR-APP] Social Sign In V2 Request: POST ' + fullUrl
            );
            logger.debug('[PAIR-APP] Public Token:', publicToken);

            const response = await fetch(fullUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-MyApiTokenHeaderPublic': publicToken,
                    'X-MyApiTokenHeader': this.getStoredJwtToken(),
                },
                body: formData,
                credentials: 'include',
            });

            const result = await response.json();

            logger.debug('[PAIR-APP] Raw V2 server response:', result);
            logger.debug('[PAIR-APP] V2 Response structure:', {
                success: result.success,
                hasToken: !!result.token,
                hasData: !!result.data,
                dataKeys: result.data ? Object.keys(result.data) : [],
                tokenLength: result.token ? result.token.length : 0,
                tokenAtRoot: result.token
                    ? result.token.substring(0, 20) + '...'
                    : 'none',
            });

            // Check if this is a "requiresSignup" response with profile data
            if (!result.success && result.requiresSignup && result.profile) {
                logger.debug(
                    '[PAIR-APP] Social Sign In V2 - User needs signup, returning with profile'
                );
                return {
                    success: false,
                    requiresSignup: true,
                    profile: result.profile,
                    error: result.error,
                };
            }

            if (!result.success) {
                logger.debug(
                    '[PAIR-APP] Social Sign In V2 failed:',
                    result.error
                );
                return {
                    __isApiError: true,
                    success: false,
                    error: result.error || 'Social sign in failed',
                    errorType: 'social_auth_error',
                    status: response.status,
                    data: undefined,
                };
            }

            // Extract and store JWT token - check both possible locations
            const tokenFromResponse =
                result.token || (result.data && result.data.token);

            if (tokenFromResponse) {
                this.jwtToken = tokenFromResponse;
                logger.debug(
                    '[PAIR-APP] JWT token received from V2 response:',
                    {
                        tokenFromRoot: !!result.token,
                        tokenFromData: !!(result.data && result.data.token),
                        tokenLength: tokenFromResponse.length,
                        tokenPrefix: tokenFromResponse.substring(0, 20) + '...',
                    }
                );

                // Call setJwtToken to ensure proper storage
                this.setJwtToken(tokenFromResponse);
            } else {
                console.error(
                    '[PAIR-APP] ⚠️ No token in V2 response at root or in data!'
                );
                console.error('[PAIR-APP] Response structure:', {
                    hasData: !!result.data,
                    dataKeys: result.data ? Object.keys(result.data) : [],
                });
            }

            // Structure the response to match what the consuming code expects
            // The token should be in data.token for consistency with V1
            const finalResponse = {
                success: true,
                data: {
                    // Include all user fields from the response first
                    ...result.data,
                    // Then override/ensure critical fields are present
                    id: result.data?.id,
                    email: result.data?.email,
                    name: result.data?.display_name || result.data?.name,
                    display_name: result.data?.display_name,
                    token: tokenFromResponse, // Use the extracted token here
                    preview_pass_x_days: result.data?.preview_pass_x_days,
                },
            };

            logger.debug(
                '[PAIR-APP] Final V2 response structure being returned:',
                {
                    success: finalResponse.success,
                    hasData: !!finalResponse.data,
                    hasToken: !!finalResponse.data.token,
                    tokenValue: finalResponse.data.token
                        ? finalResponse.data.token.substring(0, 20) + '...'
                        : 'UNDEFINED',
                    tokenInData: finalResponse.data.token
                        ? finalResponse.data.token.length
                        : 0,
                    userId: finalResponse.data.id,
                    userEmail: finalResponse.data.email,
                }
            );

            // Extra verification log
            if (!finalResponse.data.token) {
                console.error(
                    '[PAIR-APP] ⚠️ CRITICAL: Token is not in finalResponse.data!'
                );
                console.error(
                    '[PAIR-APP] tokenFromResponse was:',
                    tokenFromResponse
                );
            }

            return finalResponse;
        } catch (error) {
            console.error('[PAIR-APP] Social Sign In V2 Error:', error);
            throw error;
        }
    }

    // Social login method matching the AngularJS web app implementation
    async socialSignIn(
        provider: 'google' | 'apple',
        code: string,
        userData?: {
            email?: string;
            firstName?: string;
            lastName?: string;
            oid?: string;
        },
        allowSignup?: boolean
    ): Promise<ApiResponse<User>> {
        try {
            logger.debug(`🔐 Attempting social sign in with ${provider}...`);
            logger.debug('[PAIR-APP] socialSignIn received parameters:', {
                provider,
                code: code ? 'present' : 'missing',
                userData: userData ? 'present' : 'missing',
                allowSignup,
                allowSignupType: typeof allowSignup,
            });

            // Match the AngularJS app's format for externalSignIn
            const body: any = {
                provider: provider,
                code: code,
                action: 'login',
                view: 'web', // Add view parameter as the backend expects it
            };

            // Add oid for Apple if available
            if (userData?.oid) {
                body.oid = userData.oid;
            }

            // For Apple Sign-in, ALWAYS add redirect flag to handle the case where
            // user exists on Apple's side but not in Niio (creates SocialProfile)
            // This matches the main app behavior for continuous login
            logger.debug(
                '[PAIR-APP] Checking allowSignup:',
                allowSignup,
                'type:',
                typeof allowSignup
            );
            if (provider === 'apple' || allowSignup) {
                body.redirect = 1; // Backend expects numeric 1, not boolean true
                logger.debug(
                    '[PAIR-APP] ✅ Adding redirect flag for signup support (Apple always needs it)'
                );
            } else {
                logger.debug(
                    '[PAIR-APP] ❌ NOT adding redirect flag (not Apple and allowSignup is falsy)'
                );
            }

            const fullUrl = getApiUrl() + `/users/externalSignIn?code=${code}`;
            logger.debug('[PAIR-APP] Social Sign In Request: POST ' + fullUrl);
            logger.debug('[PAIR-APP] Social Sign In Body:', body);
            logger.debug(
                '[PAIR-APP] Final body has redirect?',
                'redirect' in body ? body.redirect : 'NO'
            );

            // Convert body to URL-encoded format like the web app
            const formData = new URLSearchParams();
            Object.keys(body).forEach((key) => {
                if (body[key] !== undefined && body[key] !== null) {
                    // Convert boolean to string for URL encoding
                    const value =
                        typeof body[key] === 'boolean'
                            ? body[key].toString()
                            : body[key];
                    formData.append(key, value);
                }
            });

            logger.debug(
                '[PAIR-APP] FormData being sent:',
                formData.toString()
            );

            const response = await fetch(fullUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-MyApiTokenHeaderPublic': getPublicToken(),
                    'X-MyApiTokenHeader': this.jwtToken || getJwtToken() || '',
                },
                body: formData.toString(),
                credentials: 'include',
            });

            logger.debug(
                '[PAIR-APP] Social Sign In Response: ' +
                    response.status +
                    ' ' +
                    response.statusText
            );

            if (response.status !== 200) {
                const errorData = await response.json().catch(() => {
                    return { message: 'Unknown error' };
                });

                let errorMessage = 'Social sign in failed';
                if (errorData.message) {
                    errorMessage =
                        typeof errorData.message === 'string'
                            ? errorData.message
                            : errorData.message.text || errorMessage;
                }

                return {
                    __isApiError: true,
                    success: false,
                    error: errorMessage,
                    errorType: 'auth_error',
                    status: response.status,
                    data: undefined,
                };
            }

            const result = await response.json();
            logger.debug('[PAIR-APP] Social Sign In Response:', result);

            // Check if this is a redirect response (user doesn't exist but SocialProfile created)
            if (result.data && result.data.profile) {
                logger.debug(
                    '[PAIR-APP] Social Sign In returned profile - user needs signup'
                );
                return {
                    success: false,
                    requiresSignup: true,
                    profile: result.data.profile,
                    ticket: result.data.ticket, // The ticket to use for signup
                    error: 'User does not exist',
                };
            }

            // Check if the response indicates an error even with 200 status
            if (
                result.status === false ||
                result.error ||
                (result.message &&
                    typeof result.message === 'object' &&
                    result.message.text)
            ) {
                const errorMessage =
                    result.error ||
                    (result.message && result.message.text) ||
                    'Social sign in failed';
                return {
                    __isApiError: true,
                    success: false,
                    error: errorMessage,
                    errorType: 'auth_error',
                    status: response.status,
                    data: undefined,
                };
            }

            // Extract JWT token from response and store it
            const jwtToken = result.token || (result.data && result.data.token);
            if (jwtToken) {
                logger.debug(
                    '[PAIR-APP] JWT Token received from V1 social login:',
                    {
                        tokenLength: jwtToken.length,
                        tokenPrefix: jwtToken.substring(0, 20) + '...',
                    }
                );
                // Use setJwtToken to ensure proper storage
                this.setJwtToken(jwtToken);
            } else {
                console.warn(
                    '[PAIR-APP] ⚠️ No token in V1 social login response!'
                );
            }

            return {
                success: true,
                data: {
                    id: (result.data && result.data.id) || result.id,
                    email:
                        userData?.email ||
                        (result.data && result.data.email) ||
                        result.email,
                    name:
                        (result.data && result.data.display_name) ||
                        result.display_name,
                    token: jwtToken,
                    preview_pass_x_days:
                        result.data?.preview_pass_x_days ||
                        result.preview_pass_x_days,
                },
            };
        } catch (error) {
            console.error(`❌ Failed to sign in with ${provider}:`, error);
            return {
                __isApiError: true,
                success: false,
                error: `Failed to sign in with ${provider}`,
                errorType: 'network_error',
                status: 0,
                data: undefined,
            };
        }
    }

    // Check if social user exists
    async getSocialProfileStatus(
        provider: 'google' | 'apple',
        oid: string
    ): Promise<ApiResponse<{ exists: boolean }>> {
        try {
            logger.debug(
                `🔍 Checking if ${provider} user exists with oid:`,
                oid
            );

            const body = {
                provider: provider,
                oid: oid,
            };

            const fullUrl = getApiUrl() + '/users/getSocialProfileStatus';
            logger.debug('[PAIR-APP] API Request: POST ' + fullUrl);

            const response = await fetch(fullUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-MyApiTokenHeaderPublic': getPublicToken(),
                    'X-MyApiTokenHeader': this.jwtToken || getJwtToken(),
                },
                body: JSON.stringify(body),
                credentials: 'include',
            });

            logger.debug(
                '[PAIR-APP] API Response: ' +
                    response.status +
                    ' ' +
                    response.statusText
            );

            if (response.status !== 200) {
                logger.debug(
                    '📛 Social profile check failed with status:',
                    response.status
                );
                return {
                    success: false,
                    error: 'Failed to check social profile status',
                    data: undefined,
                };
            }

            const result = await response.json();
            logger.debug('[PAIR-APP] Social profile status result:', result);

            // Based on mobile app logic: status === 1 means user exists
            return {
                success: true,
                data: {
                    exists: result.status === 1,
                },
            };
        } catch (error) {
            console.error('❌ Failed to check social profile status:', error);
            return {
                success: false,
                error: 'Failed to check social profile status',
                data: undefined,
            };
        }
    }

    // Get user details
    async getUserDetails(userId: string): Promise<ApiResponse<any>> {
        try {
            logger.debug('👤 Fetching user details for:', userId);

            // getUserDetails expects a GET request (uses session for user ID)
            const fullUrl = getApiUrl() + '/users/getUserDetails';
            logger.debug('[PAIR-APP] API Request: GET ' + fullUrl);

            const response = await fetch(fullUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-MyApiTokenHeaderPublic': getPublicToken(),
                    'X-MyApiTokenHeader': this.jwtToken || getJwtToken(),
                },
                credentials: 'include',
            });

            const result = await response.json();
            logger.debug('[PAIR-APP] getUserDetails Response:', result);

            // Check if the response indicates an error
            if (!response.ok || (result && result.status === 'error')) {
                return {
                    __isApiError: true,
                    success: false,
                    error: result.text || 'Failed to fetch user details',
                    status: response.status,
                    data: undefined,
                };
            }

            logger.debug('✅ User details fetched successfully');
            return {
                success: true,
                data: result,
            };
        } catch (error) {
            console.error('❌ Failed to fetch user details:', error);
            return {
                success: false,
                error: 'Failed to fetch user details',
                data: undefined,
            };
        }
    }

    // Update user profile (for account type selection)
    async updateUserProfile(
        userId: string,
        userInfo: any,
        data: { titles?: string[]; organization_type?: string },
        options?: { skipSessionRefresh?: boolean }
    ): Promise<ApiResponse<any>> {
        try {
            logger.debug('📝 Updating user profile with userInfo:', userInfo);
            logger.debug('📝 Additional data:', data);

            // Include required fields to satisfy validation
            const userData: any = {
                User: {
                    id: userId,
                    email: userInfo.email || '',
                    first_name: userInfo.first_name || '',
                    last_name: userInfo.last_name || '',
                    country: userInfo.country || 'US', // Default country if not set
                    ...data,
                },
                // Skip session refresh to avoid race conditions with concurrent API calls
                skipSessionRefresh: options?.skipSessionRefresh ?? false,
            };

            logger.debug('📝 Final userData being sent:', userData);

            const result = await this.makeRequest(
                'POST',
                '/users/updateDetails',
                userData
            );

            // Check if this is an error response
            if (result.__isApiError) {
                logger.debug('📛 Profile update returned error:', result);
                return result;
            }

            logger.debug('✅ Profile updated successfully:', result);
            return {
                success: true,
                data: result.data || result,
            };
        } catch (error) {
            console.error('❌ Profile update failed:', error);
            return {
                success: false,
                error: 'Failed to update profile',
                data: undefined,
            };
        }
    }

    // V2 Get external profile with proper Google OAuth validation
    async getExternalProfileV2(
        provider: 'google' | 'apple',
        code: string,
        appleProfile?: any,
        providerData?: { firstName?: string; lastName?: string; oid?: string }
    ): Promise<ApiResponse<any>> {
        try {
            logger.debug(`📋 Getting external profile V2 from ${provider}...`);

            const data: any = {
                provider: provider,
                code: code,
                action: 'profile',
                view: 'web',
            };

            if (appleProfile) {
                data.profile = appleProfile;
            }

            if (providerData) {
                data.firstName = providerData.firstName;
                data.lastName = providerData.lastName;
                data.oid = providerData.oid;
            }

            const formData = new URLSearchParams();
            Object.keys(data).forEach((key) => {
                if (data[key] !== undefined && data[key] !== null) {
                    if (typeof data[key] === 'object') {
                        formData.append(key, JSON.stringify(data[key]));
                    } else {
                        formData.append(key, data[key]);
                    }
                }
            });

            const fullUrl = getApiUrl() + `/users/getExternalProfileV2`;
            logger.debug(
                '[PAIR-APP] Get External Profile V2 Request: POST ' + fullUrl
            );

            const response = await fetch(fullUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: formData,
                credentials: 'include',
            });

            const result = await response.json();
            logger.debug(
                '[PAIR-APP] Get External Profile V2 Response:',
                result
            );

            return result;
        } catch (error) {
            console.error('[PAIR-APP] Get External Profile V2 Error:', error);
            throw error;
        }
    }

    // Get external profile data for social signup - matching AngularJS implementation
    async getExternalProfile(
        provider: 'google' | 'apple',
        code: string,
        appleProfile?: any,
        providerData?: { firstName?: string; lastName?: string; oid?: string }
    ): Promise<ApiResponse<any>> {
        try {
            logger.debug(`📋 Getting external profile from ${provider}...`);

            const data: any = {
                provider: provider,
                code: code,
                action: 'profile',
                view: 'web', // Add view parameter for consistency
            };

            if (appleProfile) {
                // from failed Apple login - need to send back the profile (Apple can't auth twice with same token)
                data.profile = appleProfile;
            }

            if (providerData) {
                data.firstName = providerData.firstName;
                data.lastName = providerData.lastName;
                data.oid = providerData.oid;
            }

            // Convert to URL-encoded format for consistency
            const formData = new URLSearchParams();
            Object.keys(data).forEach((key) => {
                if (data[key] !== undefined && data[key] !== null) {
                    formData.append(key, data[key]);
                }
            });

            const fullUrl = getApiUrl() + `/users/externalSignIn?code=${code}`;
            const response = await fetch(fullUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-MyApiTokenHeaderPublic': getPublicToken(),
                    'X-MyApiTokenHeader': this.jwtToken || getJwtToken() || '',
                },
                body: formData.toString(),
                credentials: 'include',
            });

            if (response.status !== 200) {
                const errorData = await response
                    .json()
                    .catch(() => ({ message: 'Unknown error' }));
                return {
                    __isApiError: true,
                    success: false,
                    error:
                        errorData.message?.text ||
                        errorData.message ||
                        'Failed to get external profile',
                    status: response.status,
                    data: undefined,
                };
            }

            const result = await response.json();

            if (result.__isApiError) {
                logger.debug('📛 Failed to get external profile:', result);
                return result;
            }

            return {
                success: true,
                data: result.data || result,
            };
        } catch (error) {
            console.error(
                `❌ Failed to get external profile from ${provider}:`,
                error
            );
            return {
                success: false,
                error: `Failed to get profile from ${provider}`,
            };
        }
    }

    // Save queue for later
    async saveQueueForLater(queuePlaylist: any): Promise<ApiResponse<any>> {
        try {
            logger.debug('💾 Saving queue for later:', queuePlaylist);
            logger.debug('💾 Queue playlist type:', typeof queuePlaylist);
            logger.debug(
                '💾 Queue playlist stringified:',
                JSON.stringify(queuePlaylist)
            );

            // Validate the queue playlist structure
            if (Array.isArray(queuePlaylist) && queuePlaylist.length > 0) {
                const firstSection = queuePlaylist[0];
                logger.debug('💾 First section:', firstSection);
                logger.debug(
                    '💾 First section artworks:',
                    firstSection.artworks
                );
                logger.debug(
                    '💾 First section artworks count:',
                    firstSection.artworks?.length
                );
            }

            // Backend now handles encoding/decoding automatically
            const result = await this.makeRequest(
                'POST',
                '/users/saveQueueForLater',
                {
                    queuePlaylist: queuePlaylist,
                    getQueueData: true,
                }
            );

            if (result.__isApiError) {
                logger.debug(
                    '📛 API returned error in saveQueueForLater:',
                    result
                );
                return result;
            }

            logger.debug('✅ Queue saved successfully');
            return { success: true, data: result };
        } catch (error) {
            console.error('❌ Failed to save queue for later:', error);
            return {
                success: false,
                error: 'Failed to save queue for later',
            };
        }
    }

    // Get saved queue for later
    async getQueueForLater(): Promise<ApiResponse<any>> {
        try {
            logger.debug('📥 Getting saved queue for later');

            const result = await this.makeRequest(
                'POST',
                '/users/getQueueForLater',
                {}
            );

            if (result.__isApiError) {
                logger.debug(
                    '📛 API returned error in getQueueForLater:',
                    result
                );
                return result;
            }

            logger.debug('✅ Got saved queue successfully:', result);

            // The backend returns the queue data with channel details
            // We just need to return it as is
            return { success: true, data: result };
        } catch (error) {
            console.error('❌ Failed to get saved queue:', error);
            return {
                success: false,
                error: 'Failed to get saved queue',
            };
        }
    }
}

const serverApi = new PairAppServerApi();

// Create a named export for the api object that includes all methods
export const api = {
    createUser: (data: {
        email: string;
        password?: string;
        deviceId: string;
        deviceCode: string;
    }) => serverApi.createUser(data),
    signInUser: (data: { email: string; password: string }) =>
        serverApi.signInUser(data),
    pairDevice: (data: {
        deviceId: string;
        deviceCode: string;
        userId: string;
    }) => serverApi.pairDevice(data),
    getFeaturedChannels: () => serverApi.getFeaturedChannels(),
    setDeviceQueue: (data: {
        deviceId: string;
        channelId: string;
        artworks?: any[];
    }) => serverApi.setDeviceQueue(data),
    startPreviewPass: () => serverApi.startPreviewPass(),
    socialSignIn: (
        provider: 'google' | 'apple',
        code: string,
        userData?: any,
        allowSignup?: boolean
    ) => serverApi.socialSignIn(provider, code, userData, allowSignup),
    socialSignInV2: (
        provider: 'google' | 'apple',
        code: string,
        userData?: any
    ) => serverApi.socialSignInV2(provider, code, userData),
    getSocialProfileStatus: (provider: 'google' | 'apple', oid: string) =>
        serverApi.getSocialProfileStatus(provider, oid),
    getExternalProfile: (
        provider: 'google' | 'apple',
        code: string,
        appleProfile?: any,
        providerData?: any
    ) =>
        serverApi.getExternalProfile(
            provider,
            code,
            appleProfile,
            providerData
        ),
    getExternalProfileV2: (
        provider: 'google' | 'apple',
        code: string,
        appleProfile?: any,
        providerData?: any
    ) =>
        serverApi.getExternalProfileV2(
            provider,
            code,
            appleProfile,
            providerData
        ),
    getUserDetails: (userId: string) => serverApi.getUserDetails(userId),
    updateUserProfile: (
        userId: string,
        userInfo: any,
        data: { titles?: string[]; organization_type?: string },
        options?: { skipSessionRefresh?: boolean }
    ) => serverApi.updateUserProfile(userId, userInfo, data, options),
    setJwtToken: (token: string) => serverApi.setJwtToken(token),
    getFeaturedCategories: () => serverApi.getFeaturedCategories(),
    getAllGenres: () => serverApi.getAllGenres(),
    saveQueueForLater: (queuePlaylist: any) =>
        serverApi.saveQueueForLater(queuePlaylist),
    getQueueForLater: () => serverApi.getQueueForLater(),
    getThemes: () => serverApi.getThemes(),
    getTopTags: (count?: number) => serverApi.getTopTags(count),
    searchChannels: (
        searchKeyword: string,
        themeIds: string[],
        tags: string[],
        limit?: number
    ) => serverApi.searchChannels(searchKeyword, themeIds, tags, limit),
    fetchGiftChannel: (channelIds: string | string[]) =>
        serverApi.fetchGiftChannel(channelIds),
    subscribeToChannels: (channelIds: string[]) =>
        serverApi.subscribeToChannels(channelIds),
    subscribeToChannel: (userId: string, channelId: string) =>
        serverApi.subscribeToChannel(userId, channelId),
    toggleWishlist: (artworkId: string) => serverApi.toggleWishlist(artworkId),
    addToFavorites: (userId: string, artworkId: string) =>
        serverApi.addToFavorites(userId, artworkId),
    associateCouponForUser: (
        provider: 'apple' | 'charge_bee',
        couponCode: string
    ) => serverApi.associateCouponForUser(provider, couponCode),
    signOut: () => serverApi.signOut(),
    getDevicesByUser: () => serverApi.getDevicesByUser(),
};

// Export individual functions for compatibility
export function createUser(data: {
    email: string;
    password?: string;
    deviceId: string;
    deviceCode: string;
    socialProvider?: string;
    socialCode?: string;
    socialTicket?: string;
}) {
    return serverApi.createUser(data);
}

export function signInUser(data: { email: string; password: string }) {
    return serverApi.signInUser(data);
}

export function pairDevice(data: {
    deviceId: string;
    deviceCode: string;
    userId: string;
}) {
    return serverApi.pairDevice(data);
}

export function getFeaturedChannels() {
    return serverApi.getFeaturedChannels();
}

export function setDeviceQueue(data: {
    deviceId: string;
    channelId: string;
    artworks?: any[];
}) {
    return serverApi.setDeviceQueue(data);
}

export function startPreviewPass() {
    return serverApi.startPreviewPass();
}

export function getFeaturedCategories() {
    return serverApi.getFeaturedCategories();
}

export function getAllGenres() {
    return serverApi.getAllGenres();
}

export function socialSignIn(
    provider: 'google' | 'apple',
    code: string,
    userData?: any
) {
    return serverApi.socialSignIn(provider, code, userData);
}

export function getSocialProfileStatus(
    provider: 'google' | 'apple',
    oid: string
) {
    return serverApi.getSocialProfileStatus(provider, oid);
}

export function getExternalProfile(
    provider: 'google' | 'apple',
    code: string,
    appleProfile?: any,
    providerData?: any
) {
    return serverApi.getExternalProfile(
        provider,
        code,
        appleProfile,
        providerData
    );
}

export function socialSignInV2(
    provider: 'google' | 'apple',
    code: string,
    userData?: any
) {
    return serverApi.socialSignInV2(provider, code, userData);
}

export function getExternalProfileV2(
    provider: 'google' | 'apple',
    code: string,
    appleProfile?: any,
    providerData?: any
) {
    return serverApi.getExternalProfileV2(
        provider,
        code,
        appleProfile,
        providerData
    );
}

export function saveQueueForLater(queuePlaylist: any) {
    return serverApi.saveQueueForLater(queuePlaylist);
}

export function getQueueForLater() {
    return serverApi.getQueueForLater();
}
